import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { ReactSVG } from 'react-svg';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import { useIntl } from 'react-intl';
import messages from './messages';

import { getStyles } from './styles';
import { Box, Breadcrumbs, Button, IconButton, Link, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { ArrowBack, NavigateNextRounded } from '@mui/icons-material';

import { IconSvg } from '../../../../utils/globalIcons';
import { useAppContext } from '../../../../context/AppContext';
import getAnalyticsDataSourceData from '../../../../utils/ApiFunctions/AnalyticsDataSourceData';
import { setAnalyticsDataSourcesState, setAnalyticsFilesState } from '../../../../Redux/slice/dataSlice';
import AnalyticsDataSetsApi from '../../../../Api/Services/AppflyteBackend/AnalyticsDataSets';
import { tostAlert } from '../../../../utils/AlertToast';
import AmeyaAuthApi from '../../../../Api/Services/AppflyteAuth/AmeyaAuthService';
import AnalyticsFilesData from '../../../../utils/ApiFunctions/AnalyticsFilesData';

import Outlook from './Datasource/outlook';
import OneDrive from './Datasource/one_drive';
import GoogleDrive from './Datasource/google_drive';
import Gmail from './Datasource/gmail';
import MongoDB from './Datasource/mongodb';
import Links from './Datasource/links';
import AmeyaExtract from './Datasource/ameya_extract';
import Files from './Datasource/files';
import AnalyticsApi from '../../../../Api/Services/AmeyaBackend/AnalyticsToolApi';
import { setAnalyticsDataSetAdded, setAnalyticsDataSourcesAdded } from '../../../../Redux/slice/newDataSlice';
import LoadBar from '../../../../utils/LoadBar';
import MonitoringServiceApi from '../../../../Api/Services/AmeyaBackend/MonitoringServiceApi';
import { fetchSubscriptionId, fetchUserEmail } from '../../../../utils/GetAccountDetails';
import getSubscriptionById from '../../../../utils/ApiFunctions/getSubscriptionDataById';
import { getAgentApiToken } from '../../../../utils';
import Appflyte from './Datasource/appflyte';
import AnalyticsDataSourcesApi from '../../../../Api/Services/AppflyteBackend/AnalyticsDataSources';

const INITIAL_STATES = {
    googledrive: {
        googledriveAvailable: false,
        googledriveConnector: {},
        googledriveFolders: [],
        googledriveSelectedFolders: null,
        accessToken: null,
        refreshToken: null,
        loading: false,
        initLoading: false,
        formErrors: {
            folderSelected: null,
            import_action: null
        },
        importAction: 'New file is created in source folder',
        importInterval: 'one_minute',
        errorStatus: false,
        errorMessage: ''
    },
    onedrive: {
        onedriveAvailable: false,
        onedriveConnector: {},
        onedriveFolders: [],
        onedriveSelectedFolders: null,
        accessToken: null,
        refreshToken: null,
        loading: false,
        initLoading: false,
        formErrors: {
            folderSelected: null,
            import_action: null
        },
        importAction: 'New file is created in source folder',
        importInterval: 'one_minute',
        errorStatus: false,
        errorMessage: ''
    },
    gmail: {
        gmailAvailable: false,
        gmailConnector: {},
        accessToken: null,
        refreshToken: null,
        loading: false,
        initLoading: false,
        subjectMessage: '',
        importInterval: 'one_minute',
        errorStatus: false,
        errorMessage: ''
    },
    outlook: {
        outlookAvailable: false,
        outlookConnector: {},
        accessToken: null,
        refreshToken: null,
        loading: false,
        initLoading: false,
        subjectMessage: '',
        importInterval: 'one_minute',
        errorStatus: false,
        errorMessage: ''
    },
    links: {
        tempUrl: "",
        pendingUrls: [],
        links: [],
        loading: false,
        initLoading: false,
        importInterval: 'one_hour',
        syncNow: false,
        linkAvailable: false,
        errorStatus: false,
        errorMessage: ''
    },
    files: {
        fileAvailable: false,
        fileList: [],
        fileConnectorData: {},
        loading: false,
        initLoading: false,
        fileAdded: false,
        errorStatus: false,
        errorMessage: ''
    },
    mongodb: {
        mongodbAvailable: false,
        connectionName: '',
        connectionStatus: '',
        connectionMongoUri: '',
        connectionMongoDb: null,
        loading: false,
        initLoading: false,
        errorStatus: false,
        errorMessage: ''
    },
    ameya_extract: {
        ameyaExtractAvailable: false,
        connectionName: '',
        connectionStatus: '',
        connectionMongoUri: '',
        connectionMongoDb: null,
        loading: false,
        initLoading: false,
        errorStatus: false,
        errorMessage: ''
    },
    appflyte: {
        appflyteAvailable: false,
        loading: false,
        appflyte_schemas: [],
        selected_schemas: [],
        initLoading: false,
        errorStatus: false,
        errorMessage: ''
    }
};

const INTERVAL_SECONDS = {
    one_minute: 60,
    five_minute: 300,
    ten_minute: 600,
    twenty_minute: 1200,
    thirty_minute: 1800,

    one_hour: 3600,
    two_hour: 7200,
    three_hour: 10800,
    four_hour: 14400,
    five_hour: 18000,
    six_hour: 21600,

    one_day: 86400,
    one_week: 604800,

    daily: 86400,
    weekly: 604800,
    monthly: 2592000,   // 30 days
    yearly: 31536000    // 365 days
};

const TAB_ICONS = {
    google_drive: IconSvg.gDriveIcon,
    one_drive: IconSvg.onedriveIcon,
    files: IconSvg.textFileIcon,
    aws_s3: IconSvg.awsIcon,
    links: IconSvg.wwwIcon,
    ameya_extract: IconSvg.ameyaIcon,
    mongodb: IconSvg.mongoIcon,
    gmail: IconSvg.mailIcon,
    outlook: IconSvg.mailIcon
};

function DatasetEdit() {
    const theme = useTheme();
    const styles = getStyles(theme);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const intl = useIntl();
    const { selectedProject } = useAppContext();

    const [tabValue, setTabValue] = useState('');
    const [loading, setLoading] = useState(false);
    const isLoading = Object.values(INITIAL_STATES).some(state => typeof state === 'object' &&
        Object.values(state).some(innerState => innerState?.loading === true)
    );
    const all_projects = useSelector(state => state.all_data.projects);

    const isInitLoading = Object.values(INITIAL_STATES).some(state => typeof state === 'object' &&
        Object.values(state).some(innerState => innerState?.initLoading === true)
    );

    const [dataSources, setDataSources] = useState([]);
    const [datasetDetails, setDatasetDetails] = useState({})
    const [exceAnalytics, setExceAnalytics] = useState(false)
    const [sourceTypeData, setSourceTypeData] = useState([]);
    const [datasetId, setDatasetId] = useState(null);
    const [datasetName, setDatasetName] = useState('');

    const [googledriveData, setGoogledriveData] = useState(INITIAL_STATES.googledrive);
    const [onedriveData, setOnedriveData] = useState(INITIAL_STATES.onedrive);
    const [linksData, setLinksData] = useState(INITIAL_STATES.links);
    const [gmailData, setGmailData] = useState(INITIAL_STATES.gmail);
    const [outlookData, setOutlookData] = useState(INITIAL_STATES.outlook);
    const [fileData, setFileData] = useState(INITIAL_STATES.files);
    const [mongodbData, setMongodbData] = useState(INITIAL_STATES.mongodb);
    const [ameyaExtractData, setAmeyaExtractData] = useState(INITIAL_STATES.ameya_extract);
    const [appflyteData, setAppflyteData] = useState(INITIAL_STATES.appflyte);

    useEffect(() => {
        const dataset_body = location.state?.dataset_body ?? null;
        const dataset_id = dataset_body?.payload?.__auto_id__ ?? null;
        const dataset_name = dataset_body?.payload?.dataset_name ?? null;
        const excel_analytics_status = dataset_body?.payload?.excel_analytics_status ?? false;

        setDatasetName(dataset_name)
        setDatasetDetails(dataset_body)
        setExceAnalytics(excel_analytics_status)
        setDatasetId(dataset_id)
        localStorage.setItem('dataset_id', JSON.stringify(dataset_id));

        if (!dataset_body || !dataset_id) {
            navigate('/data-sets');
            return;
        }
        fetchData(dataset_body);
    }, [navigate]);


    const fetchData = async (dataset_body) => {
        setLoading(true)
        try {
            await fetchSources()

            const source_types = dataset_body?.payload?.source_types ?? [];
            setSourceTypeData(source_types)

            const source_types_data = dataset_body?.payload?.source_details ?? [];
            const defaultTab = (source_types_data || [])?.at(0)?.source_type ?? null;
            setTabValue(defaultTab);

            (source_types_data || []).forEach(item => {
                const source_type = item?.source_type ?? null;
                switch (source_type) {
                    case 'mongodb':
                        setMongodbData(prev => ({
                            ...prev,
                            mongodbAvailable: true,
                            errorStatus: item?.error_status,
                            errorMessage: item?.error_status && item?.error_message
                        }));
                        break;
                    case 'one_drive':
                        setOnedriveData(prev => ({
                            ...prev,
                            onedriveAvailable: true,
                            errorStatus: item?.error_status,
                            errorMessage: item?.error_status && item?.error_message
                        }));
                        break;
                    case 'google_drive':
                        setGoogledriveData(prev => ({
                            ...prev,
                            googledriveAvailable: true,
                            errorStatus: item?.error_status,
                            errorMessage: item?.error_status && item?.error_message
                        }));
                        break;
                    case 'gmail':
                        setGmailData(prev => ({
                            ...prev,
                            gmailAvailable: true,
                            errorStatus: item?.error_status,
                            errorMessage: item?.error_status && item?.error_message
                        }));
                        break;
                    case 'outlook':
                        setOutlookData(prev => ({
                            ...prev,
                            outlookAvailable: true,
                            errorStatus: item?.error_status,
                            errorMessage: item?.error_status && item?.error_message
                        }));
                        break;
                    case 'files':
                        setFileData(prev => ({
                            ...prev,
                            fileAvailable: true,
                            errorStatus: item?.error_status,
                            errorMessage: item?.error_status && item?.error_message
                        }));
                        break;
                    case 'links':
                        setLinksData(prev => ({
                            ...prev,
                            linkAvailable: true,
                            errorStatus: item?.error_status,
                            errorMessage: item?.error_status && item?.error_message
                        }));
                        break;
                    case 'ameya_extract':
                        setAmeyaExtractData(prev => ({
                            ...prev,
                            ameyaExtractAvailable: true,
                            errorStatus: item?.error_status,
                            errorMessage: item?.error_status && item?.error_message
                        }));
                        break;
                    case 'appflyte':
                        setAppflyteData(prev => ({
                            ...prev,
                            appflyteAvailable: true,
                            errorStatus: item?.error_status,
                            errorMessage: item?.error_status && item?.error_message
                        }));
                        break;
                }
            });

        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const fetchSources = async () => {
        try {
            const response = await getAnalyticsDataSourceData(selectedProject?.payload?.__auto_id__);
            dispatch(setAnalyticsDataSourcesState(response))
            setDataSources(response)
            return response;
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (googledriveData.googledriveAvailable) {
            setGoogledriveData(prev => ({ ...prev, initLoading: true }));
            const handleGoogleDriveData = async () => {
                const source_details = datasetDetails?.payload?.source_details ?? [];
                const gdriveConnector = source_details.find(item => item?.source_type === "google_drive");
                if (gdriveConnector) {
                    await loadGoogleDriveFolderForEdit(gdriveConnector)
                }
            }
            setGoogledriveData(prev => ({ ...prev, initLoading: false }));
            handleGoogleDriveData()
        }
    }, [googledriveData.googledriveAvailable]);

    useEffect(() => {
        if (!onedriveData.onedriveAvailable) return;
        const handleOneDriveData = async () => {
            setOnedriveData(prev => ({ ...prev, initLoading: true }));
            try {
                const source_details = datasetDetails?.payload?.source_details ?? [];
                const onedriveConnector = source_details.find(item => item?.source_type === "one_drive");
                if (onedriveConnector) {
                    await loadOneDriveFolderForEdit(onedriveConnector);
                }
            } finally {
                setOnedriveData(prev => ({ ...prev, initLoading: false }));
            }
        };
        handleOneDriveData();
    }, [onedriveData.onedriveAvailable]);

    useEffect(() => {
        if (gmailData.gmailAvailable) {
            setGmailData(prev => ({ ...prev, initLoading: true }));
            const handleGmailData = async () => {
                const source_details = datasetDetails?.payload?.source_details ?? [];
                const gmailConnector = source_details.find(item => item?.source_type === "gmail");
                if (gmailConnector) {
                    const subject_message = gmailConnector?.subject_message ?? "";
                    setGmailData(prev => ({
                        ...prev, gmailConnector: gmailConnector,
                        subjectMessage: subject_message
                    }));
                    fetchAuthDetails(gmailConnector, 'google', setGmailData);
                }
            }
            setGmailData(prev => ({ ...prev, initLoading: false }));
            handleGmailData()
        }
    }, [gmailData.gmailAvailable]);

    useEffect(() => {
        if (outlookData.outlookAvailable) {
            setOutlookData(prev => ({ ...prev, initLoading: true }));
            const handleOutlookData = async () => {
                const source_details = datasetDetails?.payload?.source_details ?? [];
                const outlookConnector = source_details.find(item => item?.source_type === "outlook");
                if (outlookConnector) {
                    const subject_message = outlookConnector?.subject_message ?? "";
                    setOutlookData(prev => ({
                        ...prev, outlookConnector: outlookConnector,
                        subjectMessage: subject_message
                    }));
                    fetchAuthDetails(outlookConnector, 'azure', setOutlookData);
                }
            }
            setOutlookData(prev => ({ ...prev, initLoading: false }));
            handleOutlookData()
        }
    }, [outlookData.outlookAvailable]);

    useEffect(() => {
        if (mongodbData.mongodbAvailable) {
            setMongodbData(prev => ({ ...prev, initLoading: true }));
            const source_details = datasetDetails?.payload?.source_details ?? [];
            const mongoConnector = source_details.find(item => item?.source_type === "mongodb");
            const source_id = (mongoConnector?.source_ids || [])?.at(-1) ?? null;
            const filtered_datasource = (dataSources || [])?.find(item => item?.payload?.__auto_id__ === source_id)
            const connection_name = filtered_datasource?.payload?.name ?? '';
            const connection_mongo_uri = filtered_datasource?.payload?.connection_url ?? '';
            setMongodbData(prev => ({
                ...prev,
                connectionName: connection_name,
                connectionMongoUri: connection_mongo_uri,
                initLoading: false
            }))
        }
    }, [mongodbData.mongodbAvailable])

    useEffect(() => {
        if (linksData.linkAvailable) {
            setLinksData(prev => ({ ...prev, initLoading: true }));
            const source_details = datasetDetails?.payload?.source_details ?? [];
            const linkConnector = source_details.find(item => item?.source_type === "links");
            const import_interval = linkConnector?.interval_seconds ?? "one_hour";
            const all_links = linkConnector?.all_links ?? [];
            const newLinks = (all_links || []).map((item) => ({
                id: item?.id,
                url: item?.url ?? "",
                status: item?.status ?? "Processing",
                created_at: new Date().toISOString(),
                auto_sync: item?.auto_sync ?? "off"
            }));
            setLinksData(prev => ({ ...prev, links: newLinks, initLoading: false, importInterval: import_interval }))
        }
    }, [linksData.linkAvailable])

    useEffect(() => {
        if (mongodbData.mongodbAvailable) {
            setMongodbData(prev => ({ ...prev, initLoading: true }));
            const source_details = datasetDetails?.payload?.source_details ?? [];
            const mongoConnector = source_details.find(item => item?.source_type === "mongodb");
            const source_id = (mongoConnector?.source_ids || [])?.at(-1) ?? null;
            const filtered_datasource = (dataSources || [])?.find(item => item?.payload?.__auto_id__ === source_id)
            const connection_name = filtered_datasource?.payload?.name ?? '';
            const connection_mongo_uri = filtered_datasource?.payload?.connection_url ?? '';
            setMongodbData(prev => ({
                ...prev,
                connectionName: connection_name,
                connectionMongoUri: connection_mongo_uri,
                initLoading: false
            }))
        }
    }, [mongodbData.mongodbAvailable])

    useEffect(() => {
        if (ameyaExtractData.ameyaExtractAvailable) {
            setAmeyaExtractData(prev => ({ ...prev, initLoading: true }));
            const source_details = datasetDetails?.payload?.source_details ?? [];
            const ameyaExtractConnector = source_details.find(item => item?.source_type === "ameya_extract");
            const source_id = (ameyaExtractConnector?.source_ids || [])?.at(-1) ?? null;
            const filtered_datasource = (dataSources || [])?.find(item => item?.payload?.__auto_id__ === source_id)
            const connection_name = filtered_datasource?.payload?.name ?? '';
            const connection_mongo_uri = filtered_datasource?.payload?.connection_url ?? '';
            setAmeyaExtractData(prev => ({
                ...prev,
                connectionName: connection_name,
                connectionMongoUri: connection_mongo_uri,
                initLoading: false
            }))
        }
    }, [ameyaExtractData.ameyaExtractAvailable])

    useEffect(() => {
        const fetchFiles = async () => {
            setFileData(prev => ({ ...prev, initLoading: true }));
            const source_details = datasetDetails?.payload?.source_details ?? [];
            const fileConnector = source_details.find(item => item?.source_type === "files");
            if (fileConnector) {
                try {
                    await getAllAnalyticsSourceFiles(fileConnector);
                } catch (error) {
                    console.error('Error fetching files:', error);
                }
            }
            setFileData(prev => ({ ...prev, initLoading: false, fileConnectorData: fileConnector }));
        };
        if (fileData.fileAvailable) {
            fetchFiles();
        }
    }, [fileData.fileAvailable, fileData.fileAdded]);

    useEffect(() => {
        if (appflyteData.appflyteAvailable) {
            setAppflyteData(prev => ({ ...prev, initLoading: true }));

            const source_details = datasetDetails?.payload?.source_details ?? [];
            const appflyteConnector = source_details.find(item => item?.source_type === "appflyte");
            const schema_ids = (appflyteConnector?.schema_ids || []) ?? [];
            const matchingProjects = all_projects.filter(project => schema_ids.includes(project.payload?.__auto_id__)) ?? [];
            setAppflyteData(prev => ({
                ...prev,
                appflyte_schemas: matchingProjects,
                selected_schemas: matchingProjects,
                initLoading: false
            }))
        }
    }, [appflyteData.appflyteAvailable])

    const updateDatasource = async (data) => {
        try {
            const integration_id = data?.payload?.__auto_id__;
            const update_key = data?.update_key;

            const reqObj = {
                "id": integration_id,
                "fields": [
                    { "path": "$.auth_status", "value": "expired" },
                    { "path": "$.updated_on", "value": moment().format('YYYY-MM-DD HH:mm:ss') }
                ]
            }
            const res = await AnalyticsDataSourcesApi.update(JSON.stringify(reqObj), integration_id, update_key);
            if (res.status === 200) {
                dispatch(setAnalyticsDataSourcesAdded(true))
            }
            return
        } catch (error) {
            console.log(error)
            return
        }
    }

    // Google Folder Data
    const fetchGoogleFolderDetails = useCallback(async (item) => {
        setGoogledriveData(prev => ({ ...prev, loading: true }));
        try {

            const source_id = item?.source_id ?? null;
            const filtered_datasource = (dataSources || [])?.find(ds => ds?.payload?.__auto_id__ === source_id) ?? null;
            if (!filtered_datasource) {
                tostAlert(intl.formatMessage(messages.source_error), 'error');
                navigate("/data-sets")
                return
            }

            const refresh_token = filtered_datasource?.payload?.refresh_token;
            if (!refresh_token) throw new Error('No refresh token');

            const tokenRes = await AmeyaAuthApi.getTokens(refresh_token, 'google');
            const tokenData = tokenRes.data ?? null;
            if (!tokenData) {
                await updateDatasource(filtered_datasource)
                setGoogledriveData(prev => ({ ...prev, loading: false }));
                tostAlert(intl.formatMessage(messages.source_token_error), 'error');
                navigate("/sources/analytics-tool/integrations")
                return
            }

            const { access_token, refresh_token: new_refresh_token } = tokenData;

            const reqObj = { access_token };
            const driveRes = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), 'gdrive');

            const rootFolders = driveRes.data.folders.map(folder => ({
                id: folder.id,
                label: folder.name,
                depth: 0,
                hasChildren: folder.hasChildren,
                childrenLoaded: false
            }));

            setGoogledriveData(prev => ({
                ...prev,
                googledriveFolders: rootFolders,
                accessToken: access_token,
                refreshToken: new_refresh_token,
                loading: false
            }));

        } catch (err) {
            console.error(err);
            setGoogledriveData(prev => ({ ...prev, loading: false }));
        }
    }, []);

    const fetchGoogleSubFolders = async (parentFolder, accessToken) => {
        const reqObj = {
            access_token: accessToken,
            parent_id: parentFolder.id
        };

        const response = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), 'gdrive');
        const children = response.data.folders.map(folder => ({
            id: folder.id,
            label: folder.name,
            parentId: parentFolder.id,
            depth: (parentFolder.depth || 0) + 1,
            hasChildren: folder.hasChildren,
            expanded: false,
            childrenLoaded: false
        }));

        setGoogledriveData(prev => {
            const index = prev.googledriveFolders.findIndex(f => f.id === parentFolder.id);
            if (index === -1) return prev;

            return {
                ...prev,
                googledriveFolders: [
                    ...prev.googledriveFolders.slice(0, index + 1),
                    ...children,
                    ...prev.googledriveFolders.slice(index + 1)
                ]
            };
        });
    };

    const expandGooglePath = async (path, accessToken) => {
        for (let i = 0; i < path.length - 1; i++) {
            const folder = path[i];

            await fetchGoogleSubFolders(folder, accessToken);
            setGoogledriveData(prev => ({
                ...prev,
                googledriveFolders: prev.googledriveFolders.map(f => f.id === folder.id ? { ...f, expanded: true, childrenLoaded: true } : f)
            }));
        }

        setGoogledriveData(prev => ({ ...prev, googledriveSelectedFolders: path[path.length - 1] }));
    };

    const loadGoogleDriveFolderForEdit = async (connector) => {
        const folderId = connector?.folder_id;
        if (!folderId) return;

        setGoogledriveData(prev => ({ ...prev, loading: true }));

        try {

            const source_id = connector?.source_id ?? null;
            const filtered_datasource = (dataSources || [])?.find(item => item?.payload?.__auto_id__ === source_id) ?? null;

            if (!filtered_datasource) {
                tostAlert(intl.formatMessage(messages.source_error), 'error');
                navigate("/data-sets")
                return
            }

            const refresh_token = filtered_datasource?.payload?.refresh_token;
            if (!refresh_token) throw new Error('No refresh token');

            const tokenRes = await AmeyaAuthApi.getTokens(refresh_token, 'google');
            const tokenData = tokenRes.data ?? null;
            if (!tokenData) {
                await updateDatasource(filtered_datasource)
                setGoogledriveData(prev => ({ ...prev, loading: false }));
                tostAlert(intl.formatMessage(messages.source_token_error), 'error');
                navigate("/sources/analytics-tool/integrations")
                return
            }

            const { access_token, refresh_token: new_refresh_token } = tokenData;

            const rootRes = await AmeyaAuthApi.getFoldersDetails(JSON.stringify({ access_token }), 'gdrive');
            const rootFolders = rootRes.data.folders.map(f => ({
                id: f.id,
                label: f.name,
                depth: 0,
                hasChildren: f.hasChildren,
                expanded: false,
                childrenLoaded: false
            }));

            setGoogledriveData(prev => ({
                ...prev,
                googledriveFolders: rootFolders,
                accessToken: access_token,
                refreshToken: new_refresh_token,
                googledriveConnector: connector
            }));

            const pathRes = await AmeyaAuthApi.getGoogleDriveFolderPath({ folder_id: folderId, access_token });
            await expandGooglePath(pathRes.data, access_token);

        } catch (e) {
            console.error(e);
        } finally {
            setGoogledriveData(prev => ({ ...prev, loading: false }));
        }
    };

    // Onedrive Folder data
    const fetchOneDriveFolderDetails = useCallback(async (item) => {
        setOnedriveData(prev => ({ ...prev, loading: true }));

        try {

            const source_id = item?.source_id ?? null;
            const filtered_datasource = (dataSources || [])?.find(ds => ds?.payload?.__auto_id__ === source_id) ?? null;
            if (!filtered_datasource) {
                tostAlert(intl.formatMessage(messages.source_error), 'error');
                navigate("/data-sets")
                return
            }

            const refresh_token = filtered_datasource?.payload?.refresh_token;
            if (!refresh_token) throw new Error('No refresh token');

            const response = await AmeyaAuthApi.getTokens(refresh_token, 'azure');
            const tokenData = response.data ?? null;
            if (!tokenData) {
                await updateDatasource(filtered_datasource)
                setOnedriveData(prev => ({ ...prev, loading: false }));
                tostAlert(intl.formatMessage(messages.source_token_error), 'error');
                navigate("/sources/analytics-tool/integrations")
                return
            }

            const { access_token, refresh_token: new_refresh_token } = tokenData || {};
            if (!access_token || !new_refresh_token) {
                throw new Error('Invalid tokens received');
            }

            const reqObj = { access_token };
            const drive_response = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), 'onedrive');

            if (drive_response.status !== 200) {
                throw new Error(`Failed to fetch folders: ${drive_response.status}`);
            }

            const all_folders = drive_response.data?.folders || [];
            if (all_folders.length === 0) {
                console.warn('No folders found in OneDrive');
            }

            const formattedFolders = all_folders.map(folder => ({
                ...folder,
                label: folder.label || folder.name || folder.id,
                id: folder.id
            }));

            setOnedriveData(prev => ({
                ...prev,
                onedriveFolders: formattedFolders,
                accessToken: access_token,
                refreshToken: new_refresh_token,
                loading: false
            }));

            return formattedFolders;

        } catch (error) {
            console.error('OneDrive folder fetch error:', error);
            tostAlert(intl.formatMessage(messages.onedrive_folder_error), 'error');
            setOnedriveData(prev => ({
                ...prev,
                loading: false,
                formErrors: { ...prev.formErrors, folderSelected: 'Failed to load folders' }
            }));
        }
    }, []);

    const fetchOnedriveSubFolders = async (parentFolder, access_token) => {
        const reqObj = { access_token, parent_id: parentFolder.id };
        const response = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), 'onedrive');

        const subfolders = response.data.folders.map(folder => ({
            ...folder,
            parentId: parentFolder.id,
            depth: (parentFolder.depth || 0) + 1,
            expanded: false,
            childrenLoaded: false
        }));

        setOnedriveData(prev => {
            const index = prev.onedriveFolders.findIndex(f => f.id === parentFolder.id);
            if (index === -1) return prev;

            return {
                ...prev,
                onedriveFolders: [
                    ...prev.onedriveFolders.slice(0, index + 1),
                    ...subfolders,
                    ...prev.onedriveFolders.slice(index + 1)
                ]
            };
        });
    };

    const expandPath = async (path, accessToken) => {
        for (let i = 0; i < path.length - 1; i++) {
            const folder = path[i];
            await fetchOnedriveSubFolders(folder, accessToken);
            setOnedriveData(prev => ({
                ...prev,
                onedriveFolders: prev.onedriveFolders.map(f => f.id === folder.id ? { ...f, expanded: true, childrenLoaded: true } : f)
            }));
        }
        const selected = path[path.length - 1];
        setOnedriveData(prev => ({
            ...prev,
            onedriveSelectedFolders: selected
        }));
    };

    const loadOneDriveFolderForEdit = async (onedriveConnector) => {
        const folderId = onedriveConnector?.folder_id;
        if (!folderId) return;

        setOnedriveData(prev => ({ ...prev, loading: true }));

        try {

            const source_id = onedriveConnector?.source_id ?? null;
            const filtered_datasource = (dataSources || [])?.find(item => item?.payload?.__auto_id__ === source_id) ?? null;

            if (!filtered_datasource) {
                tostAlert(intl.formatMessage(messages.source_error), 'error');
                navigate("/data-sets")
                return
            }

            const refresh_token = filtered_datasource?.payload?.refresh_token;
            if (!refresh_token) throw new Error('No refresh token');

            const tokenResp = await AmeyaAuthApi.getTokens(refresh_token, 'azure');
            const tokenData = tokenResp.data ?? null;
            if (!tokenData) {
                await updateDatasource(filtered_datasource)
                setOnedriveData(prev => ({ ...prev, loading: false }));
                tostAlert(intl.formatMessage(messages.source_token_error), 'error');
                navigate("/sources/analytics-tool/integrations")
                return
            }

            const { access_token, refresh_token: new_refresh_token } = tokenData;

            const rootResp = await AmeyaAuthApi.getFoldersDetails(JSON.stringify({ access_token }), 'onedrive');
            const rootFolders = rootResp.data.folders.map(f => ({
                ...f,
                depth: 0,
                expanded: false,
                childrenLoaded: false
            }));

            setOnedriveData(prev => ({
                ...prev,
                onedriveFolders: rootFolders,
                accessToken: access_token,
                refreshToken: new_refresh_token,
                onedriveConnector: onedriveConnector
            }));

            const pathResp = await AmeyaAuthApi.getOnedriveFolderPath({ folder_id: folderId, access_token });
            await expandPath(pathResp.data, access_token);

        } catch (e) {
            console.error(e);
        } finally {
            setOnedriveData(prev => ({ ...prev, loading: false }));
        }
    };

    const fetchAuthDetails = async (item, provider, setState) => {
        setState(prev => ({ ...prev, loading: true }));

        try {

            const source_id = item?.source_id ?? null;
            const filtered_datasource = (dataSources || [])?.find(ds => ds?.payload?.__auto_id__ === source_id) ?? null;

            if (!filtered_datasource) {
                tostAlert(intl.formatMessage(messages.source_error), 'error');
                navigate("/data-sets")
                return
            }

            const refresh_token = filtered_datasource?.payload?.refresh_token;
            if (!refresh_token) throw new Error('No refresh token available');

            const response = await AmeyaAuthApi.getTokens(refresh_token, provider);
            const tokenData = response.data ?? null;
            if (!tokenData) {
                await updateDatasource(filtered_datasource)
                setState(prev => ({ ...prev, loading: false }));
                tostAlert(intl.formatMessage(messages.source_token_error), 'error');
                navigate("/sources/analytics-tool/integrations")
                return
            }

            const { access_token, refresh_token: new_refresh_token } = tokenData || {};
            if (!access_token || !new_refresh_token) {
                throw new Error('Invalid tokens received');
            }

            setState(prev => ({
                ...prev,
                accessToken: access_token,
                refreshToken: new_refresh_token,
                loading: false
            }));

        } catch (error) {
            console.error(`${provider} auth error:`, error);
            tostAlert(`Failed to load ${provider} token. Please try again.`, 'error');
            setState(prev => ({ ...prev, loading: false }));
        }
    }

    function checkStatus(fileItems, statusItems) {
        const statusMap = Object.fromEntries(
            statusItems.map(item => [
                item.file_id, {
                    status: item.status ?? "Processing",
                    checksum: item.checksum ?? null,
                    new_file: item.new_file ?? true
                }
            ])
        );
        return fileItems.map(item => ({
            ...item,
            payload: {
                ...item.payload,
                status: statusMap[item.payload.file_id]?.status ?? "Processing",
                new_file: statusMap[item.payload.file_id]?.new_file ?? true,
                checksum: statusMap[item.payload.file_id]?.checksum ?? null
            }
        }));
    }

    const getAllAnalyticsSourceFiles = async (fileConnector) => {
        setFileData(prev => ({ ...prev, loading: true }));
        try {

            const response = await AnalyticsFilesData(datasetId);

            const connector_files = fileConnector?.files || [];
            const updated_files = checkStatus(response, connector_files);
            setFileData(prev => ({ ...prev, fileAdded: false }));

            if (exceAnalytics) {
                const filtered_response = updated_files?.filter(item => {
                    const url = item?.payload?.file_url?.toLowerCase();
                    const cleanUrl = url?.split('?')[0];
                    return cleanUrl?.endsWith('.xlsx');
                });

                setFileData(prev => ({ ...prev, fileList: filtered_response }));
                return filtered_response;

            } else {
                const filtered_response = updated_files?.filter(item => {
                    const url = item?.payload?.file_url?.toLowerCase();
                    const cleanUrl = url?.split('?')[0];
                    return !cleanUrl?.endsWith('.xlsx');
                });

                setFileData(prev => ({ ...prev, fileList: filtered_response }));
                return filtered_response;
            }

        } catch (error) {
            console.error('Error fetching analytics files:', error);
            tostAlert(intl.formatMessage(messages.file_load_error), 'error');
            return [];
        } finally {
            setFileData(prev => ({ ...prev, loading: false }));
        }
    }

    function validateDataSources() {

        const errors = [];

        if (sourceTypeData.includes('google_drive')) {
            if (!googledriveData.googledriveSelectedFolders) {
                setTabValue('google_drive');
                errors.push(intl.formatMessage(messages.gdrive_add_error));
            }
            if (!googledriveData.importAction) {
                setTabValue('google_drive');
                errors.push('Please select import action');
            }
        }

        if (sourceTypeData.includes('one_drive')) {
            if (!onedriveData.onedriveSelectedFolders) {
                setTabValue('one_drive');
                errors.push(intl.formatMessage(messages.onedrive_add_error));
            }
            if (!onedriveData.importAction) {
                setTabValue('one_drive');
                errors.push('Please select import action');
            }
        }

        if (sourceTypeData.includes('gmail') && !gmailData.subjectMessage?.trim()) {
            setTabValue('gmail');
            errors.push(intl.formatMessage(messages.gmail_subject_message_error));
        }

        if (sourceTypeData.includes('outlook') && !outlookData.subjectMessage?.trim()) {
            setTabValue('outlook');
            errors.push(intl.formatMessage(messages.outlook_subject_message_error));
        }

        if (sourceTypeData.includes('files') && !fileData.fileList?.length) {
            setTabValue('files');
            errors.push(intl.formatMessage(messages.file_select_error));
        }

        if (sourceTypeData.includes('links') && !linksData.links?.length) {
            setTabValue('links');
            errors.push(intl.formatMessage(messages.url_add_error));
        }

        if (sourceTypeData.includes('appflyte') && appflyteData.selected_schemas?.length === 0) {
            setTabValue('appflyte');
            errors.push(intl.formatMessage(messages.appflyte_project_message_error));
        }

        if (errors.length > 0) {
            tostAlert(errors[0], 'warning');
            return false;
        }

        return true;
    }

    const handleMongoDBDatasetUpdate = async () => {
        try {

            const source_details = datasetDetails?.payload?.source_details ?? [];
            const dataset_id = datasetDetails?.payload?.dataset_id ?? null;
            const dataset_item_id = datasetDetails?.payload?.__auto_id__ ?? null;
            const dataset_update_key = datasetDetails?.update_key ?? null;

            const mongoConnector = source_details.find(item => item?.source_type === "mongodb");
            const source_ids = (mongoConnector?.source_ids || []) ?? [];

            if (source_ids.length === 0) {
                tostAlert(intl.formatMessage(messages.mongo_source_error), 'warning');
                return;
            }

            const reqObj = {
                dataset_id: dataset_id,
                dataset_name: datasetName,
                source_ids: source_ids
            };

            const datasetResponse = await AnalyticsApi.updateDataset(JSON.stringify(reqObj));
            if (datasetResponse.status !== 200) {
                tostAlert(intl.formatMessage(messages.dataset_update_error), 'error');
                return;
            }

            const dataObject = {
                "id": dataset_item_id,
                "fields": [{ "path": "$.dataset_name", "value": datasetName }]
            };

            const updateResponse = await AnalyticsDataSetsApi.update(JSON.stringify(dataObject), dataset_item_id, dataset_update_key);
            if (updateResponse.status === 200) {
                tostAlert(intl.formatMessage(messages.dataset_update_success), "success");
                localStorage.removeItem("dataset_id")
                dispatch(setAnalyticsDataSetAdded(true))
                navigate('/data-sets');
            } else {
                throw new Error('Failed to update dataset');
            }

        } catch (error) {
            console.log(error)
        }
    }

    const handleAmeyaExtractDatasetUpdate = async () => {
        try {

            const source_details = datasetDetails?.payload?.source_details ?? [];
            const dataset_id = datasetDetails?.payload?.dataset_id ?? null;
            const dataset_item_id = datasetDetails?.payload?.__auto_id__ ?? null;
            const dataset_update_key = datasetDetails?.update_key ?? null;

            const ameyaExtractConnector = source_details.find(item => item?.source_type === "ameya_extract");
            const source_ids = (ameyaExtractConnector?.source_ids || []) ?? [];

            if (source_ids.length === 0) {
                tostAlert('No valid sources found', 'warning');
                return;
            }

            const reqObj = {
                dataset_id: dataset_id,
                dataset_name: datasetName,
                source_ids: source_ids
            };

            const datasetResponse = await AnalyticsApi.updateDataset(JSON.stringify(reqObj));
            if (datasetResponse.status !== 200) {
                tostAlert(intl.formatMessage(messages.dataset_update_error), 'error');
                return;
            }

            const dataObject = {
                "id": dataset_item_id,
                "fields": [{ "path": "$.dataset_name", "value": datasetName }]
            };

            const updateResponse = await AnalyticsDataSetsApi.update(JSON.stringify(dataObject), dataset_item_id, dataset_update_key);
            if (updateResponse.status === 200) {
                tostAlert(intl.formatMessage(messages.dataset_update_success), "success");
                localStorage.removeItem("dataset_id")
                dispatch(setAnalyticsDataSetAdded(true))
                navigate('/data-sets');
            } else {
                throw new Error('Failed to update dataset');
            }

        } catch (error) {
            console.log(error)
        }
    }

    const handleAppflyteDatasetUpdate = async () => {
        try {

            const dataset_id = datasetDetails?.payload?.dataset_id ?? null;
            const dataset_item_id = datasetDetails?.payload?.__auto_id__ ?? null;
            const dataset_update_key = datasetDetails?.update_key ?? null;

            const schema_ids = (appflyteData.selected_schemas || []).map(item => item?.payload?.__auto_id__) ?? [];

            const reqObj = {
                dataset_id: dataset_id,
                dataset_name: datasetName,
                source_ids: schema_ids
            };

            const datasetResponse = await AnalyticsApi.updateDataset(JSON.stringify(reqObj));
            if (datasetResponse.status !== 200) {
                tostAlert(intl.formatMessage(messages.dataset_update_error), 'error');
                return;
            }

            const dataObject = {
                "id": dataset_item_id,
                "fields": [
                    {
                        "path": "$.dataset_name",
                        "value": datasetName
                    },
                    {
                        "path": "$.source_ids",
                        "value": schema_ids
                    }
                ]
            };

            const updateResponse = await AnalyticsDataSetsApi.update(JSON.stringify(dataObject), dataset_item_id, dataset_update_key);
            if (updateResponse.status === 200) {
                tostAlert(intl.formatMessage(messages.dataset_update_success), "success");
                localStorage.removeItem("dataset_id")
                dispatch(setAnalyticsDataSetAdded(true))
                navigate('/data-sets');
            } else {
                throw new Error('Failed to update dataset');
            }

        } catch (error) {
            console.log(error)
        }
    }

    const getCloudAuthConfig = async () => {
        const needsCloudAuth = sourceTypeData.some(name => ['google_drive', 'one_drive', 'gmail', 'outlook'].includes(name));

        if (!needsCloudAuth) {
            return { google_auth: {}, azure_auth: {} };
        }

        try {
            const subscription_id = await fetchSubscriptionId();
            if (!subscription_id) {
                throw new Error('Subscription ID not found');
            }

            const subscriptions = await getSubscriptionById(subscription_id);
            const subscriptionData = subscriptions?.payload;
            if (!subscriptionData) {
                throw new Error('Subscription data not found');
            }

            const authConfig = subscriptionData.ameya_auth_config || {};
            const google_auth = authConfig.google || {};
            const azure_auth = authConfig.azure || {};

            const needsGoogle = sourceTypeData.some(name => ['google_drive', 'gmail'].includes(name));
            const needsAzure = sourceTypeData.some(name => ['one_drive', 'outlook'].includes(name));

            if (needsGoogle && (!google_auth.client_id || !google_auth.client_secret)) {
                throw new Error('Google authentication configuration is incomplete');
            }

            if (needsAzure && (!azure_auth.client_id || !azure_auth.client_secret || !azure_auth.tenant_id)) {
                throw new Error('Azure authentication configuration is incomplete');
            }

            return { google_auth, azure_auth };

        } catch (error) {
            console.error('Authentication configuration error:', error);
            tostAlert(`Authentication error: ${error.message}`, 'warning');
            return null;
        }
    }

    const getIntervalSeconds = (interval) => {
        return INTERVAL_SECONDS[interval] || 0;
    }

    const createDataSourceHandlers = () => ({
        google_drive: async (sourceTypesData, sourceDetailsData, connectionObj, authConfig) => {
            const source_details = datasetDetails?.payload?.source_details ?? [];
            const filtered_source_id = source_details.find(item => item?.source_type === "google_drive")?.source_id ?? null;
            if (!filtered_source_id) return false;

            const { accessToken, refreshToken, googledriveSelectedFolders, importInterval } = googledriveData;
            if (!accessToken || !refreshToken) {
                tostAlert(intl.formatMessage(messages.gdrive_token_error), 'warning');
                return false;
            }

            const interval_seconds = getIntervalSeconds(importInterval);
            const folder_id = googledriveSelectedFolders?.id || googledriveSelectedFolders;
            const job_id = uuid();

            connectionObj.google_drive = [{
                job_id: job_id,
                client_id: authConfig.google_auth.client_id,
                folder_id,
                connection_id: filtered_source_id,
                access_token: accessToken,
                client_secret: authConfig.google_auth.client_secret,
                refresh_token: refreshToken,
                interval_seconds
            }];

            sourceTypesData.push('google_drive');
            sourceDetailsData.push({
                source_type: 'google_drive',
                interval_seconds,
                job_id: job_id,
                source_id: filtered_source_id,
                folder_id,
                refresh_token: refreshToken,
            })

            return true;
        },

        one_drive: async (sourceTypesData, sourceDetailsData, connectionObj, authConfig) => {

            const source_details = datasetDetails?.payload?.source_details ?? [];
            const filtered_source_id = source_details.find(item => item?.source_type === "one_drive")?.source_id ?? null;
            if (!filtered_source_id) return false;

            const { accessToken, refreshToken, onedriveSelectedFolders, importInterval } = onedriveData;
            if (!accessToken || !refreshToken) {
                tostAlert(intl.formatMessage(messages.onedrive_token_error), 'warning');
                return false;
            }

            const one_drive_data = (dataSources || [])?.find(item => item?.payload?.__auto_id__ === filtered_source_id)
            const interval_seconds = getIntervalSeconds(importInterval);
            const folder_id = onedriveSelectedFolders?.id || onedriveSelectedFolders;
            const job_id = uuid();

            connectionObj.one_drive = [{
                job_id: job_id,
                user_id: one_drive_data?.payload?.email,
                folder_id,
                client_id: authConfig.azure_auth.client_id,
                tenant_id: authConfig.azure_auth.tenant_id,
                connection_id: one_drive_data?.payload?.__auto_id__,
                access_token: accessToken,
                client_secret: authConfig.azure_auth.client_secret,
                refresh_token: refreshToken,
                interval_seconds
            }];

            sourceTypesData.push('one_drive');
            sourceDetailsData.push({
                source_type: 'one_drive',
                interval_seconds,
                job_id: job_id,
                source_id: one_drive_data?.payload?.__auto_id__,
                folder_id,
                user_id: one_drive_data?.payload?.email,
                refresh_token: refreshToken
            });

            return true;
        },

        gmail: async (sourceTypesData, sourceDetailsData, connectionObj, authConfig) => {
            const source_details = datasetDetails?.payload?.source_details ?? [];
            const gmail_data = source_details.find(item => item?.source_type === "gmail") ?? null;
            if (!gmail_data) return false;

            const { accessToken, refreshToken, subjectMessage, importInterval } = gmailData;
            if (!accessToken || !refreshToken) {
                tostAlert(intl.formatMessage(messages.google_token_error), 'warning');
                return false;
            }

            const interval_seconds = getIntervalSeconds(importInterval);
            const job_id = uuid();
            const target_emails = gmail_data?.target_emails ?? [];
            const source_id = gmail_data?.source_id ?? null;

            connectionObj.google_email = {
                job_id: job_id,
                client_id: authConfig.google_auth.client_id,
                connection_id: source_id,
                access_token: accessToken,
                client_secret: authConfig.google_auth.client_secret,
                refresh_token: refreshToken,
                subject_message: subjectMessage,
                target_emails: target_emails,
                interval_seconds
            };

            sourceTypesData.push('gmail');
            sourceDetailsData.push({
                source_type: 'gmail',
                interval_seconds,
                source_id: job_id,
                target_emails: target_emails,
                subject_message: subjectMessage,
                refresh_token: refreshToken,
            });

            return true;
        },

        outlook: async (sourceTypesData, sourceDetailsData, connectionObj, authConfig) => {

            const source_details = datasetDetails?.payload?.source_details ?? [];
            const filtered_source_id = source_details.find(item => item?.source_type === "outlook")?.source_id ?? null;
            if (!filtered_source_id) return false;

            const { accessToken, refreshToken, subjectMessage, importInterval } = outlookData;
            if (!accessToken || !refreshToken) {
                tostAlert(intl.formatMessage(messages.outlook_token_error), 'warning');
                return false;
            }

            const outlook_data = (dataSources || [])?.find(item => item?.payload?.__auto_id__ === filtered_source_id)
            const interval_seconds = getIntervalSeconds(importInterval);
            const job_id = uuid();

            connectionObj.outlook_email = {
                job_id: job_id,
                tenant_id: authConfig.azure_auth.tenant_id,
                client_id: authConfig.azure_auth.client_id,
                access_token: accessToken,
                connection_id: outlook_data?.payload?.__auto_id__,
                refresh_token: refreshToken,
                client_secret: authConfig.azure_auth.client_secret,
                subject_message: subjectMessage,
                target_emails: [outlook_data?.payload?.email],
                interval_seconds
            };

            sourceTypesData.push('outlook');
            sourceDetailsData.push({
                source_type: 'outlook',
                interval_seconds,
                job_id: job_id,
                source_id: outlook_data?.payload?.__auto_id__,
                target_emails: [outlook_data?.payload?.email],
                subject_message: subjectMessage,
                refresh_token: refreshToken
            });

            return true;
        },

        links: async (sourceTypesData, sourceDetailsData, connectionObj) => {

            const source_details = datasetDetails?.payload?.source_details ?? [];
            const filtered_source_id = source_details.find(item => item?.source_type === "links")?.source_id ?? null;
            if (!filtered_source_id) return false;

            const { links, importInterval } = linksData;
            const updated_links = (links || []).filter(item => item?.url?.trim()).map(item => ({ url: item.url.trim() }));
            const interval_seconds = getIntervalSeconds(importInterval);
            const job_id = uuid();

            if (updated_links.length === 0) {
                tostAlert(intl.formatMessage(messages.links_error), 'warning');
                return false;
            }

            connectionObj.links = {
                job_id: job_id,
                connection_id: filtered_source_id,
                urls: updated_links,
                interval_seconds: interval_seconds
            };

            sourceTypesData.push('links');
            sourceDetailsData.push({
                source_type: 'links',
                interval_seconds: importInterval,
                job_id: job_id,
                source_id: filtered_source_id,
                all_links: links
            });

            return true;
        },

        files: async (sourceTypesData, sourceDetailsData, connectionObj) => {

            const source_details = datasetDetails?.payload?.source_details ?? [];
            const filtered_source_id = source_details.find(item => item?.source_type === "files")?.source_id ?? null;
            if (!filtered_source_id) return false;

            const selectedFiles = fileData.fileList || [];
            const job_id = uuid();
            const updated_file_data = selectedFiles.filter(item => item?.payload?.file_url).map(item => item?.payload?.file_url?.split("?")[0]);
            const updated_file_details = selectedFiles.filter(item => item?.payload?.file_url).map(item => ({
                "file_id": item.payload.__auto_id__,
                "file_name": item.payload.file_name,
                "status": "Processing"
            }));

            if (updated_file_data.length === 0) {
                tostAlert(intl.formatMessage(messages.file_select_error), 'warning');
                return false;
            }

            connectionObj.direct_files = {
                job_id: job_id,
                bucket_name: window.__ENV__.S3_BUCKET_NAME,
                files: updated_file_data,
                connection_id: filtered_source_id
            };

            sourceTypesData.push('files');
            sourceDetailsData.push({
                job_id: job_id,
                source_type: 'files',
                source_id: filtered_source_id,
                bucket_name: window.__ENV__.S3_BUCKET_NAME,
                files: updated_file_details
            });

            return true;
        }
    })

    const handleFileDatasetUpdate = async () => {
        try {

            const source_details = datasetDetails?.payload?.source_details ?? [];
            const job_ids = (source_details || [])?.map(item => item?.job_id) ?? [];

            if (job_ids.length === 0) {
                tostAlert('No valid sources found', 'warning');
                return;
            }

            try {
                const response = await MonitoringServiceApi.deleteJobs(JSON.stringify({ "job_ids": job_ids }));
                if (![200, 201].includes(response.status)) {
                    throw new Error("Failed to update dataset");
                }
            } catch (error) {
                const failedDeletions = error?.response?.data?.detail?.failed_deletions ?? error?.response?.detail?.failed_deletions ?? [];
                const anyJobNotFound = failedDeletions.some((item) => item?.error === "Job not found" && item?.status === "not_found");
                if (!anyJobNotFound) {
                    throw new Error("Failed to update dataset");
                }
            }

            const dataset_id = datasetDetails?.payload?.dataset_id ?? null;
            const dataset_item_id = datasetDetails?.payload?.__auto_id__ ?? null;
            const dataset_update_key = datasetDetails?.update_key ?? null;
            const project_id = selectedProject?.payload?.__auto_id__;
            const alertEmail = await fetchUserEmail();

            const connectionObj = {};
            const sourceTypesData = [];
            const sourceDetailsData = [];

            const authConfig = await getCloudAuthConfig();
            if (!authConfig) return;

            const DATA_SOURCE_HANDLERS = createDataSourceHandlers();

            for (const sourceName of sourceTypeData) {
                const handler = DATA_SOURCE_HANDLERS[sourceName];
                if (handler) {
                    const result = await handler(sourceTypesData, sourceDetailsData, connectionObj, authConfig);
                    if (!result) return;
                } else {
                    console.warn(`Unknown data source: ${sourceName}`);
                }
            }

            const agentTokenId = await getAgentApiToken()

            const analyticsObj = {
                "auth_token": agentTokenId,
                "dataset_id": dataset_id,
                "dataset_name": datasetName,
                "alert_email": alertEmail,
                "job_type": "an",
                "is_excel": false,
                "connections": connectionObj
            }

            const response = await MonitoringServiceApi.createJob(JSON.stringify(analyticsObj), project_id)
            if (response.status !== 200 && response.status !== 201) {
                tostAlert(intl.formatMessage(messages.dataset_update_error), "error");
                return
            }

            const dataObject = {
                "id": dataset_item_id,
                "fields": [
                    { "path": "$.dataset_name", "value": datasetName },
                    { "path": "$.source_types", "value": sourceTypesData },
                    { "path": "$.status", "value": 'Processing' },
                    { "path": "$.alert_email", "value": alertEmail },
                    { "path": "$.source_details", "value": sourceDetailsData },
                    { "path": "$.updated_on", "value": moment().format('YYYY-MM-DD HH:mm:ss') }
                ]
            }

            const addResponse = await AnalyticsDataSetsApi.update(JSON.stringify(dataObject), dataset_item_id, dataset_update_key);
            if (addResponse.status === 200) {
                tostAlert(intl.formatMessage(messages.dataset_update_success), "success");
                localStorage.removeItem("dataset_id")
                dispatch(setAnalyticsDataSetAdded(true))
                navigate('/data-sets');
            } else {
                throw new Error('Failed to update dataset');
            }

        } catch (error) {
            console.error('File-based dataset error:', error);
            throw error;
        }
    }

    const handleDatasetUpdate = async () => {
        try {
            if (sourceTypeData.includes('mongodb')) {
                return await handleMongoDBDatasetUpdate();
            }
            if (sourceTypeData.includes('ameya_extract')) {
                return await handleAmeyaExtractDatasetUpdate();
            }
            if (sourceTypeData.includes('appflyte')) {
                return await handleAppflyteDatasetUpdate();
            }
            return await handleFileDatasetUpdate();

        } catch (error) {
            console.error('Dataset creation error:', error);
            tostAlert(intl.formatMessage(messages.dataset_update_error), 'error');
        }
    }

    const checkNameExistence = async () => {
        try {

            const project_id = selectedProject?.payload?.__auto_id__;
            const { data } = await AnalyticsDataSetsApi.checkNameExistence(project_id, datasetName);
            const collections = data?.published_collections_detail || [];

            const existingData = collections.flatMap(c => data[c.id] || []);
            if (!existingData?.length) return false;

            const nameExists = existingData.some(item => item?.payload?.__auto_id__ !== datasetId);
            return nameExists;

        } catch (err) {
            console.log(err);
            return false;
        }
    };

    const handleSave = async () => {
        setLoading(true)
        try {

            if (!datasetName?.trim()) {
                tostAlert(intl.formatMessage(messages.dataset_name_error), 'warning');
                return;
            }

            if (!validateDataSources()) {
                return;
            }

            const nameExists = await checkNameExistence();
            if (nameExists) {
                tostAlert(intl.formatMessage(messages.dataset_name_check), 'warning');
                return;
            }

            await handleDatasetUpdate();

        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    function formatLabel(label) {
        if (typeof label !== 'string') return '';
        return label.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }

    const handleConnectionDeselect = (tab) => {
        if (sourceTypeData.length < 2) return;

        const updatedSourceTypes = sourceTypeData.filter(item => item !== tab);
        setSourceTypeData(updatedSourceTypes);

        if (tabValue === tab && updatedSourceTypes.length > 0) {
            setTabValue(updatedSourceTypes[0]);
        }
    }

    const breadcrumbs = [
        <Link
            underline="hover"
            key="1"
            color="inherit"
            onClick={() => navigate('/data-sets')}
            sx={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}
        >
            <IconButton>
                <ArrowBack sx={{ color: '#0B51C5', fontSize: '24px', fontWeight: '24px' }} />
            </IconButton>
            <Typography sx={styles.linkText}>All Datasets</Typography>
        </Link>,
        <Typography
            key="2"
            sx={{
                ...styles.linkText,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '200px'
            }}
        >
            {formatLabel(datasetName)}
        </Typography>
    ];

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>

                {(loading || isLoading || isInitLoading) && <LoadBar />}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    <Stack spacing={2} sx={{ margin: '0' }}>
                        <Breadcrumbs
                            separator={<NavigateNextRounded fontSize="small" />}
                            aria-label="breadcrumb"
                        >
                            {breadcrumbs}
                        </Breadcrumbs>
                    </Stack>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '100px' }}
                            onClick={handleSave}
                            disabled={loading || isLoading || isInitLoading}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                                SAVE
                            </Typography>
                        </Button>
                        <Button
                            onClick={(e) => navigate('/data-sets')}
                            sx={{ backgroundColor: '#DEDEDE', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#404040' }}>
                                CANCEL
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ marginTop: '20px' }}>
                    <Typography mb={1}>Dataset Name <span style={{ color: "red" }}>*</span></Typography>
                    <TextField
                        value={datasetName}
                        onChange={(e) => setDatasetName(e.target.value)}
                        size="small"
                        variant="outlined"
                        autoFocus
                        sx={{ width: '350px' }}
                    />
                </Box>

                <Box sx={{ display: 'inline-block', borderBottom: 1, borderColor: 'divider', marginTop: '15px' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(event, newValue) => setTabValue(newValue)}
                        aria-label="dynamic tabs"
                    >
                        {sourceTypeData.map((tab) => (
                            <Tab
                                key={tab}
                                value={tab}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <ReactSVG
                                            src={TAB_ICONS[tab]}
                                            beforeInjection={(svg) => {
                                                const isActive = tabValue === tab;
                                                svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                                                svg.setAttribute('stroke', isActive ? '#1976d2' : '#000000');
                                            }}
                                        />
                                        <Typography sx={{ fontSize: '16px', fontWeight: 600, textTransform: 'none' }}>
                                            {formatLabel(tab)}
                                        </Typography>

                                        {sourceTypeData.length > 1 && (
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleConnectionDeselect(tab);
                                                }}
                                                size="small"
                                            >
                                                <ReactSVG
                                                    src={IconSvg.deleteIcon}
                                                    beforeInjection={(svg) => {
                                                        const isActive = tabValue === tab;
                                                        svg.setAttribute('style', 'width:18px; height:18px; display:block;');
                                                        svg.setAttribute('stroke', isActive ? '#C30E2E' : '#000000');
                                                    }}
                                                />
                                            </IconButton>
                                        )}
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>

                <Box sx={{ marginTop: '20px' }}>

                    {tabValue === "files" && (
                        <Files
                            fileData={fileData}
                            setFileData={setFileData}
                            exceAnalytics={exceAnalytics}
                            onReload={getAllAnalyticsSourceFiles}
                        />
                    )}

                    {tabValue === "mongodb" && (
                        <MongoDB
                            loading={loading}
                            setMongodbData={setMongodbData}
                            mongodbData={mongodbData}
                            source_types={sourceTypeData}
                        />
                    )}

                    {tabValue === "one_drive" && (
                        <OneDrive
                            loading={loading}
                            onedriveData={onedriveData}
                            setOnedriveData={setOnedriveData}
                            onReload={fetchOneDriveFolderDetails}
                            fetchSubFolders={fetchOnedriveSubFolders}
                        />
                    )}

                    {tabValue === "google_drive" && (
                        <GoogleDrive
                            loading={loading}
                            googledriveData={googledriveData}
                            setGoogledriveData={setGoogledriveData}
                            onReload={fetchGoogleFolderDetails}
                            fetchGoogleSubFolders={fetchGoogleSubFolders}
                        />
                    )}

                    {tabValue === "gmail" && (
                        <Gmail
                            loading={loading}
                            gmailData={gmailData}
                            setGmailData={setGmailData}
                        />
                    )}

                    {tabValue === "outlook" && (
                        <Outlook
                            loading={loading}
                            outlookData={outlookData}
                            setOutlookData={setOutlookData}
                        />
                    )}

                    {tabValue === "links" && (
                        <Links
                            loading={loading}
                            linksData={linksData}
                            setLinksData={setLinksData}
                        />
                    )}

                    {tabValue === "ameya_extract" && (
                        <AmeyaExtract
                            setAmeyaExtractData={setAmeyaExtractData}
                            ameyaExtractData={ameyaExtractData}
                        />
                    )}

                    {tabValue === "appflyte" && (
                        <Appflyte
                            setAppflyteData={setAppflyteData}
                            appflyteData={appflyteData}
                        />
                    )}


                    {/* {tabValue === "aws_s3" && (
                        <AWS_S3
                            source_types={sourceTypeData}
                            loading={loading}
                            setDataLoading={setDataLoading}
                        />
                    )} */}

                </Box>

            </Box>
        </Box>
    )
}

export default DatasetEdit;